import { EmailData, Place } from '../../../types/results.types';
import { isReadyToContact } from '../../../utils/rowActions';

export type EmailPreviewSource = 'person' | 'company';

export interface EmailPreviewItem {
  email: EmailData;
  source: EmailPreviewSource;
  isGeneric: boolean;
  isReady: boolean;
  originalIndex: number;
}

export interface EmailPreviewResult {
  visible: EmailPreviewItem[];
  hiddenCount: number;
  totalCount: number;
  hasGuaranteed: boolean;
  hasReady: boolean;
}

const EMAIL_PREVIEW_LIMIT = 2;

function isGenericEmail(email: EmailData): boolean {
  return email.verification_status === 'GENERIC' || email.source_type === 'GENERIC' || email.is_role_account === true;
}

function getPriority(item: EmailPreviewItem): number {
  if (item.source === 'person' && !item.isGeneric) return 0;
  if (item.source === 'company' && !item.isGeneric) return 1;
  if (item.source === 'person' && item.isGeneric) return 2;
  return 3;
}

function compareEmailPreviewItems(left: EmailPreviewItem, right: EmailPreviewItem): number {
  const priorityDelta = getPriority(left) - getPriority(right);
  if (priorityDelta !== 0) return priorityDelta;

  const guaranteedDelta =
    Number(right.email.verification_status === 'GUARANTEED') -
    Number(left.email.verification_status === 'GUARANTEED');
  if (guaranteedDelta !== 0) return guaranteedDelta;

  const readyDelta = Number(right.isReady) - Number(left.isReady);
  if (readyDelta !== 0) return readyDelta;

  return left.originalIndex - right.originalIndex;
}

export function buildEmailPreview(place: Place, limit = EMAIL_PREVIEW_LIMIT): EmailPreviewResult {
  const byEmail = new Map<string, EmailPreviewItem>();
  let originalIndex = 0;

  const addEmail = (email: EmailData, source: EmailPreviewSource) => {
    const normalizedEmail = email.email.trim().toLowerCase();
    if (!normalizedEmail || byEmail.has(normalizedEmail)) return;

    byEmail.set(normalizedEmail, {
      email,
      source,
      isGeneric: isGenericEmail(email),
      isReady: isReadyToContact(email),
      originalIndex,
    });
    originalIndex += 1;
  };

  (place.people ?? []).forEach((person) => {
    (person.emails ?? []).forEach((email) => addEmail(email, 'person'));
  });
  (place.company_emails ?? []).forEach((email) => addEmail(email, 'company'));

  const sorted = Array.from(byEmail.values()).sort(compareEmailPreviewItems);
  const visible = sorted.slice(0, limit);

  return {
    visible,
    hiddenCount: Math.max(0, sorted.length - visible.length),
    totalCount: sorted.length,
    hasGuaranteed: visible.some((item) => item.email.verification_status === 'GUARANTEED'),
    hasReady: visible.some((item) => item.isReady),
  };
}
